// packages/app-shell/src/architecture-view/view-indexes.ts
//
// Index builders for the sidebar/details panel. Mirror the inline useMemos in
// App.tsx (the first consumer); extracted here so ArchitectureView's opt-in
// chrome can render the same Sidebar without dragging in the whole App. Per
// the repo's copy-first rule this is a deliberate second copy — when a third
// consumer appears, fold App's memos onto these helpers too.

import { moduleKey, flattenInterfaces } from "@archlang/scene/arch-model";
import { processKey } from "../processKey.js";
import type { ArchitectureModel, Module, Process, Interface, StableId } from "@archlang/scene/arch-model";
import type { BoardSpace } from "@archlang/scene/board";
import { ancestryTest, portHalves } from "../board/ports.js";
import type { Indexes, BoardIndexes, ConnRef } from "../components/Sidebar.js";

/** Model-level lookup tables (modules, interfaces, processes by id). */
export function buildModelIndexes(model: ArchitectureModel | null): Indexes | null {
  if (!model) return null;
  const moduleById = new Map<string, Module>();
  const moduleParentById = new Map<string, StableId>();
  const moduleQualifiedNameById = new Map<string, string>();
  const interfaceById = new Map<string, { iface: Interface; moduleId: StableId }>();
  const processById = new Map<string, Process>();

  const visit = (m: Module, parentPath = "", parentKey?: StableId) => {
    const mKey = moduleKey(m);
    moduleById.set(mKey, m);
    if (parentKey) moduleParentById.set(mKey, parentKey);
    const fullPath = parentPath ? `${parentPath}.${m.name}` : m.name;
    moduleQualifiedNameById.set(mKey, fullPath);
    for (const iface of flattenInterfaces(m)) {
      interfaceById.set(`${fullPath}.${iface.name}`, { iface, moduleId: mKey as StableId });
    }
    for (const c of m.children) visit(c, fullPath, mKey as StableId);
  };
  for (const m of model.modules) visit(m);
  for (const p of model.processes) processById.set(processKey(p), p);

  return { moduleById, moduleParentById, moduleQualifiedNameById, interfaceById, processById };
}

/** Board-level connection indexes (edges/ports per module + interface). */
export function buildBoardIndexes(
  root: BoardSpace | null,
  indexes: Indexes | null,
): BoardIndexes {
  const connByModule = new Map<string, ConnRef[]>();
  const processesByModule = new Map<string, Set<string>>();
  const connByInterface = new Map<string, ConnRef[]>();

  const push = (m: Map<string, ConnRef[]>, k: string, ref: ConnRef) => {
    const arr = m.get(k) ?? [];
    arr.push(ref);
    m.set(k, arr);
  };
  const noteIface = (ifaceId: string | undefined, ref: ConnRef) => {
    if (!ifaceId) return;
    push(connByInterface, ifaceId, ref);
  };
  const noteProcesses = (moduleId: string, details: readonly { processId?: string }[]) => {
    let set = processesByModule.get(moduleId);
    for (const d of details) {
      if (!d.processId) continue;
      if (!set) {
        set = new Set();
        processesByModule.set(moduleId, set);
      }
      set.add(d.processId);
    }
  };
  const labelOf = (id: string) => indexes?.moduleById.get(id)?.name ?? id;
  const under = ancestryTest(indexes?.moduleParentById);

  const walk = (space: BoardSpace) => {
    for (const e of space.edges ?? []) {
      const fromRef: ConnRef = { edgeId: e.id, peerId: e.to, peerLabel: labelOf(e.to), direction: "out", details: e.details };
      const toRef: ConnRef = { edgeId: e.id, peerId: e.from, peerLabel: labelOf(e.from), direction: "in", details: e.details };
      push(connByModule, e.from, fromRef);
      push(connByModule, e.to, toRef);
      noteProcesses(e.from, e.details);
      noteProcesses(e.to, e.details);
      for (const d of e.details) {
        noteIface(d.fromInterfaceId, fromRef);
        noteIface(d.toInterfaceId, toRef);
      }
    }
    for (const item of space.items) {
      for (const port of item.externalPorts ?? []) {
        for (const [direction, details] of portHalves(port, under)) {
          const ref: ConnRef = { edgeId: port.id, peerId: port.peerId, peerLabel: labelOf(port.peerId), direction, details };
          push(connByModule, item.id, ref);
          noteProcesses(item.id, details);
          for (const d of details) {
            noteIface(direction === "out" ? d.fromInterfaceId : d.toInterfaceId, ref);
          }
        }
      }
      if (item.internalCalls?.length) {
        const ref: ConnRef = { edgeId: item.id, peerId: item.id, peerLabel: item.label, direction: "internal", details: item.internalCalls };
        push(connByModule, item.id, ref);
        noteProcesses(item.id, item.internalCalls);
        for (const d of item.internalCalls) {
          noteIface(d.fromInterfaceId, ref);
          noteIface(d.toInterfaceId, ref);
        }
      }
      if (item.space) walk(item.space);
    }
  };
  if (root) walk(root);
  return { connByModule, processesByModule, connByInterface };
}
